import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { MainLayout } from '@/components/Layout';

// Public pages
import BetaInvite from '@/pages/BetaInvite';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import SignupSuccess from '@/pages/SignupSuccess';
import EmailVerified from '@/pages/EmailVerified';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import PolicyPage from '@/pages/PolicyPage';
import AccountDisabled from '@/pages/AccountDisabled';

// Protected pages
import OrgDashboard from '@/pages/Dashboard/OrgDashboard';
import SiteDashboard from '@/pages/Dashboard/SiteDashboard';
import SitesList from '@/pages/Sites/SitesList';
import SiteSettings from '@/pages/Sites/SiteSettings';
import SiteEventLog from '@/pages/Dashboard/SiteEventLog';
import TeamMembers from '@/pages/Team/TeamMembers';
import OrgSettings from '@/pages/Settings/OrgSettings';

function App() {
  const { checkAuth, isAuthenticated, hasHydrated } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Only wait for hydration - isLoading is for button states, not app blocking
  if (!hasHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<BetaInvite />} />
        <Route path="/landing" element={<Landing />} />
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
        />
        <Route
          path="/signup"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Signup />}
        />
        <Route path="/signup-success" element={<SignupSuccess />} />
        <Route path="/email-verified" element={<EmailVerified />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/account-disabled" element={<AccountDisabled />} />
        <Route
          path="/privacy"
          element={<PolicyPage docId="24126a64-ba17-4e87-a233-ad9d9c682efe" />}
        />
        <Route
          path="/terms"
          element={<PolicyPage docId="89c9016c-b186-44c3-bfed-da1a4ffd6cd1" />}
        />
        <Route
          path="/disclaimer"
          element={<PolicyPage docId="b85cffef-9cb2-4510-9d69-1eb629dbbb85" />}
        />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<OrgDashboard />} />
          <Route path="/sites" element={<SitesList />} />
          <Route path="/sites/:siteId" element={<SiteDashboard />} />
          <Route path="/sites/:siteId/events" element={<SiteEventLog />} />
          <Route path="/sites/:siteId/settings" element={<SiteSettings />} />
          <Route path="/team" element={<TeamMembers />} />
          <Route path="/billing" element={<Navigate to="/settings?tab=subscription" replace />} />
          <Route path="/settings" element={<OrgSettings />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
